import { SyncItemType, type Task, TaskStatus } from "@taskfortress/core"
import React, { useCallback, useMemo, useState } from "react"
import { useData } from "../../providers/DataProvider"
import { cn } from "../../styles/utils"
import { type PropsWithClassname } from "../../types"
import { Box } from "../Box"
import { IconSwapButton } from "../IconSwapButton"
import { Icon } from "../Icons"
import { Pressable } from "../Pressable"
import { Text } from "../Text"
import { TaskModal } from "./TaskModal"
import { HIT_SLOP_MEDIUM } from "./constants"

interface TaskItemProps extends PropsWithClassname {
  task: Task
  textClassName?: string
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  className,
  textClassName,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { saveItems, deleteItem } = useData()

  // Memoize the status toggle handler
  const handleToggleStatus = useCallback(async () => {
    const newStatus =
      task.status === TaskStatus.TODO ? TaskStatus.DONE : TaskStatus.TODO

    try {
      const updatedTask: Task = {
        ...task,
        status: newStatus,
        updatedAt: new Date().toISOString(),
      }
      await saveItems([
        {
          itemId: task.id,
          type: SyncItemType.Task,
          content: updatedTask,
        },
      ])
    } catch (error) {
      console.error("Failed to update task status:", error)
    }
  }, [task, saveItems])

  // Memoize the press handler
  const handlePress = useCallback(() => {
    // Default behavior: open edit modal
    setIsModalVisible(true)
  }, [])

  // Handle closing the modal
  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false)
  }, [])

  // Handle saving the task
  const handleSaveTask = useCallback(
    async (updatedTask: Task) => {
      try {
        // Save the updated task using the DataProvider
        await saveItems([
          {
            itemId: task.id,
            type: SyncItemType.Task,
            content: {
              title: updatedTask.title,
              description: updatedTask.description,
              status: updatedTask.status,
              tags: updatedTask.tags,
              dueDate: updatedTask.dueDate,
            },
          },
        ])

        setIsModalVisible(false)
      } catch (error) {
        console.error("Failed to save task:", error)
      }
    },
    [task.id, saveItems],
  )

  // Handle deleting the task
  const handleDeleteTask = useCallback(
    async (taskToDelete: Task) => {
      try {
        await deleteItem(taskToDelete.id)
        setIsModalVisible(false)
      } catch (error) {
        console.error("Failed to delete task:", error)
      }
    },
    [deleteItem],
  )

  // Memoize the container className
  const containerClassName = useMemo(
    () =>
      cn(
        "rounded-lg border",
        task.status === TaskStatus.DONE
          ? "px-2 py-1 border-outline-500 bg-background-50"
          : "p-3 border-outline-600 bg-background-0",
        className,
      ),
    [task.status, className],
  )

  // Memoize the text className
  const finalTextClassName = useMemo(
    () =>
      cn(
        "font-medium line-clamp-2",
        task.status === TaskStatus.DONE
          ? "text-xs line-through text-typography-400"
          : "text-typography-900",
        textClassName,
      ),
    [task.status, textClassName],
  )

  // Memoize hitSlop object
  const hitSlop = useMemo(
    () => ({
      top: HIT_SLOP_MEDIUM,
      bottom: HIT_SLOP_MEDIUM,
      left: HIT_SLOP_MEDIUM,
      right: HIT_SLOP_MEDIUM,
    }),
    [],
  )

  return (
    <>
      <Pressable className={containerClassName} onPress={handlePress}>
        <Box className="flex-row justify-between items-center">
          <Box className="flex-1 pr-2">
            <Text className={finalTextClassName} numberOfLines={2}>
              {task.title}
            </Text>
          </Box>
          <IconSwapButton
            onPress={handleToggleStatus}
            hitSlop={hitSlop}
            defaultIcon={
              task.status === TaskStatus.TODO ? (
                <Icon name="check-circle" className="h-6 w-6 text-primary-50" />
              ) : (
                <Icon name="x-circle" className="h-6 w-6 text-typography-300" />
              )
            }
            hoverIcon={
              task.status === TaskStatus.TODO ? (
                <Icon
                  name="check-circle"
                  className="h-6 w-6 text-primary-500"
                />
              ) : (
                <Icon name="x-circle" className="h-6 w-6 text-typography-300" />
              )
            }
          />
        </Box>
      </Pressable>
      <TaskModal
        task={task}
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
      />
    </>
  )
}
